import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { FormattedMessage } from "react-intl";
import type { ICategoryItemExt } from "./CategoryCreate";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import "./CategoryCreateEditItemModal.scss";

type Props = {
    show: boolean;
    onHide: () => void;
    onSave: (category_item: ICategoryItemExt, index: number) => Promise<void>;
    category_item: ICategoryItemExt | null;
    isLoading?: boolean;
    error: string | null;
    index: number;
};

type ValidationErrors = {
    code?: string;
    name?: string;
    description?: string;
};

// Extracted validation function
const validateCategoryItemForm = (category_item: ICategoryItemExt): ValidationErrors => {
    const errors: ValidationErrors = {};

    if (!category_item.code || category_item.code.trim() === "") {
        errors.code = translate("common.required_field", "This field is required");
    }

    if (!category_item.name || category_item.name.trim() === "") {
        errors.name = translate("common.required_field", "This field is required");
    }

    return errors;
};

// Extracted form submit handler
const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>, onSave: () => Promise<void> | void) => {
    e.preventDefault();
    onSave();
};

// Extracted utility function for processing field values
const processFieldValue = (name: string, value: string): string => {
    return name === "code" ? value.trim() : value;
};

const CategoryCreateEditItemModal = (props: Props) => {
    const [item, setItem] = useState<ICategoryItemExt | null>(null);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
    const [isLoading, setIsLoading] = useState(false);
    const [hasValidationErrors, setHasValidationErrors] = useState(false);

    // A new item is one that has is_new flag set
    const is_new_item = props.category_item?.id === null;

    const validateForm = validateCategoryItemForm;

    const handleChange = useCallback(
        (name: string, value: string) => {
            const allowed_names = ["code", "name", "description"];
            if (!allowed_names.includes(name)) {
                return;
            }

            setItem(prev => {
                if (!prev) return prev;
                const new_value = processFieldValue(name, value);
                const updated = { ...prev, [name]: new_value };

                // Clear validation error for this field when user starts typing
                if (validationErrors[name]) {
                    const updated_errors: ValidationErrors = { ...validationErrors };
                    delete updated_errors[name];
                    setValidationErrors(updated_errors);
                    // Update validation state
                    setHasValidationErrors(Object.keys(updated_errors).length > 0);
                }

                return updated;
            });
        },
        [validationErrors]
    );

    const handleSave = useCallback(async () => {
        if (!item) return;

        // Validate form
        const errors = validateForm(item);
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            setHasValidationErrors(true);
            return;
        }

        setIsLoading(true);

        try {
            await props.onSave(item, props.index);
        } catch (err) {
            // We handle it in the parent component
        } finally {
            setIsLoading(false);
        }
    }, [item, props, is_new_item]);

    useEffect(() => {
        setItem(props.category_item);
        setValidationErrors({});
        setHasValidationErrors(false);
        setIsLoading(false);
    }, [props.category_item, props.show]);

    // Update validation state when item changes
    useEffect(() => {
        if (item) {
            const errors = validateForm(item);
            setHasValidationErrors(Object.keys(errors).length > 0);
        }
    }, [item]);

    if (!props.show) {
        return null;
    }

    // Enter submits the form
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        handleFormSubmit(e, handleSave);
    };

    const current_is_loading = isLoading || props.isLoading;

    return (
        <Modal
            show={props.show}
            onHide={current_is_loading ? undefined : props.onHide}
            backdrop={current_is_loading ? "static" : true}
            keyboard={!current_is_loading}
            className="category-create-edit-item-modal"
        >
            <Modal.Header>
                <Modal.Title>
                    {is_new_item ? (
                        <FormattedMessage id="Categories.create_category_item" defaultMessage="Add category item" />
                    ) : (
                        <FormattedMessage id="Categories.edit_category_item" defaultMessage="Edit category item" />
                    )}
                </Modal.Title>
                <button type="button" className="close" onClick={props.onHide}>
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">
                        <FormattedMessage id="common.close" defaultMessage="Close" />
                    </span>
                </button>
            </Modal.Header>
            <Modal.Body>
                {props.error && <ErrorComponent msg={props.error} type="error" className="mt-0" />}
                <form onSubmit={handleSubmit} id="category-create-edit-item-form">
                    <div className="row align-items-center">
                        <div className="col">
                            <div className="form-row">
                                <div className="col-auto w-100">
                                    <div className="form-group">
                                        <label htmlFor="code">
                                            {translate("common.code", "Code")} <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${validationErrors.code ? "is-invalid" : ""}`}
                                            id="code"
                                            value={item?.code || ""}
                                            onChange={e => handleChange("code", e.target.value)}
                                            disabled={item?.id !== null || current_is_loading}
                                            required
                                            maxLength={50}
                                            autoFocus={is_new_item}
                                            aria-describedby="code-error"
                                        />
                                        {validationErrors.code && (
                                            <div id="code-error" className="invalid-feedback">
                                                {validationErrors.code}
                                            </div>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="name">
                                            {translate("common.name", "Name")} <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${validationErrors.name ? "is-invalid" : ""}`}
                                            id="name"
                                            value={item?.name || ""}
                                            onChange={e => handleChange("name", e.target.value)}
                                            disabled={current_is_loading}
                                            required
                                            maxLength={100}
                                            autoFocus={!is_new_item}
                                            aria-describedby="name-error"
                                        />
                                        {validationErrors.name && (
                                            <div id="name-error" className="invalid-feedback">
                                                {validationErrors.name}
                                            </div>
                                        )}
                                    </div>
                                    <div className="form-group mb-0">
                                        <label htmlFor="description">
                                            {translate("common.description", "Description")}
                                        </label>
                                        <textarea
                                            className={`form-control description-textarea ${validationErrors.description ? "is-invalid" : ""}`}
                                            id="description"
                                            value={item?.description || ""}
                                            onChange={e => handleChange("description", e.target.value)}
                                            disabled={current_is_loading}
                                            maxLength={500}
                                            rows={3}
                                            aria-describedby="description-error"
                                        />
                                        {validationErrors.description && (
                                            <div id="description-error" className="invalid-feedback">
                                                {validationErrors.description}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-outline-secondary" onClick={props.onHide} disabled={current_is_loading}>
                    {translate("common.cancel", "Cancel")}
                </button>
                <button
                    className="btn btn-primary"
                    type="submit"
                    form="category-create-edit-item-form"
                    disabled={current_is_loading || hasValidationErrors}
                >
                    <FormattedMessage id="common.save" defaultMessage="Save" />
                    {current_is_loading && <i className="fas fa-spinner fa-spin ml-1" />}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default CategoryCreateEditItemModal;
